import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { executeNoticeAction } from '@api/services/noticeActions'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  PersonAdd,
  GroupAdd,
  Explore,
  CheckCircle,
  Info,
  Announcement,
  Settings as SettingsIcon,
  NotificationsNone,
  AdminPanelSettings,
  PlayCircle,
  DoneAll,
  OpenInFull,
  Schedule,
  Forum,
  EmojiEvents,
  PlaceOutlined,
  EditNote,
  Event,
  Groups,
  EventAvailable,
  CallSplit,
  PlaylistAddCheck,
} from '@mui/icons-material'
import { getUserNotices, markNoticeAsRead } from '@api/endpoints/notices'
import { NoticeTypeIds, NoticeDataTypes, type UserNotice } from '@api/types/notices'
import { groupSessionNoticeTarget } from '@/features/notifications/noticePresentation'

interface NotificationsDropdownProps {
  anchorEl: HTMLElement | null
  onClose: () => void
}

const DROPDOWN_LIMIT = 10

function getIconForType(notification: UserNotice): React.ReactNode {
  if (notification.iconUrl) {
    return <Avatar src={notification.iconUrl} sx={{ width: 32, height: 32 }} />
  }
  const typeUpper = notification.type.toUpperCase()
  if (notification.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST) {
    return <GroupAdd sx={{ fontSize: 22 }} />
  }
  switch (typeUpper) {
    case NoticeTypeIds.FRIEND_REQUEST.toUpperCase(): return <PersonAdd sx={{ fontSize: 22 }} />
    case NoticeTypeIds.FRIEND_REQUEST_ACCEPTED.toUpperCase(): return <CheckCircle sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GROUP_INVITATION.toUpperCase(): return <GroupAdd sx={{ fontSize: 22 }} />
    case NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase(): return <Explore sx={{ fontSize: 22 }} />
    case NoticeTypeIds.ADVENTURE_OPENED.toUpperCase(): return <Schedule sx={{ fontSize: 22 }} />
    case NoticeTypeIds.SESSION_INVITE.toUpperCase(): return <PlayCircle sx={{ fontSize: 22 }} />
    case NoticeTypeIds.DISCUSSION_REPLY.toUpperCase(): return <Forum sx={{ fontSize: 22 }} />
    case NoticeTypeIds.BADGE_EARNED.toUpperCase(): return <EmojiEvents sx={{ fontSize: 22 }} />
    case NoticeTypeIds.ADVENTURE_CHECKED_IN.toUpperCase(): return <PlaceOutlined sx={{ fontSize: 22 }} />
    case NoticeTypeIds.CONTRIBUTION_DECIDED.toUpperCase(): return <EditNote sx={{ fontSize: 22 }} />
    case NoticeTypeIds.CONTRIBUTION_RECEIVED.toUpperCase(): return <EditNote sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GROUP_DISCUSSION_STARTED.toUpperCase(): return <Forum sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GROUP_EVENT_POSTED.toUpperCase(): return <Event sx={{ fontSize: 22 }} />
    // Forming a group, and settling when it goes.
    case NoticeTypeIds.SESSION_MATCH_AVAILABLE.toUpperCase(): return <Groups sx={{ fontSize: 22 }} />
    case NoticeTypeIds.SESSION_SCHEDULE_DECIDED.toUpperCase(): return <EventAvailable sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GROUP_SESSION_UPDATE.toUpperCase(): return <Event sx={{ fontSize: 22 }} />
    case NoticeTypeIds.SESSION_SPLIT_PROPOSED.toUpperCase(): return <CallSplit sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GROUP_TODO_ACTIVITY.toUpperCase(): return <PlaylistAddCheck sx={{ fontSize: 22 }} />
    case NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase(): return <AdminPanelSettings sx={{ fontSize: 22 }} />
    case NoticeTypeIds.SYSTEM.toUpperCase(): return <SettingsIcon sx={{ fontSize: 22 }} />
    case NoticeTypeIds.GENERAL.toUpperCase(): return <Announcement sx={{ fontSize: 22 }} />
    default: return <Info sx={{ fontSize: 22 }} />
  }
}

function getTimeAgo(timestamp: string): string {
  const time = new Date(timestamp)
  if (isNaN(time.getTime()) || time.getFullYear() < 2000) return ''
  const diffMs = Date.now() - time.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return time.toLocaleDateString()
}

/**
 * Facebook-style notification dropdown anchored to the bell icon on desktop.
 * Shows the top {DROPDOWN_LIMIT} notices for a quick glance; clicking a row
 * marks it read and jumps to the full /notifications page where the user
 * can take action (accept/decline/etc.). Header has Mark-All-Read; footer
 * has a "See all" button.
 *
 * On mobile, MainLayout bypasses this and navigates straight to the full
 * page (the original behavior).
 */
/**
 * Action types the dispatcher can service. An unlisted one is not drawn at all rather than
 * becoming a button that throws — the server may send kinds this build predates.
 */
const SUPPORTED_ACTION_TYPES = new Set([
  'accept_friend_request', 'decline_friend_request', 'block_user',
  'accept_group_invite', 'decline_group_invite', 'block_group',
  'accept_session_invite', 'decline_session_invite',
  'accept_adventure_transfer', 'decline_adventure_transfer',
  'accept_trip_share', 'decline_trip_share',
])

export default function NotificationsDropdown({ anchorEl, onClose }: NotificationsDropdownProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const open = Boolean(anchorEl)

  const { data: notices = [], isLoading } = useQuery({
    queryKey: ['userNotices'],
    queryFn: getUserNotices,
    enabled: open,
  })

  const visibleNotices = useMemo(
    () => (Array.isArray(notices) ? notices.slice(0, DROPDOWN_LIMIT) : []),
    [notices],
  )
  const unreadCount = useMemo(
    () => (Array.isArray(notices) ? notices.filter((n) => !n.read).length : 0),
    [notices],
  )

  const markReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
    },
  })

  // Which row action is in flight, so the row can say so and refuse a second click.
  const [busyAction, setBusyAction] = useState<{ noticeId: string; index: number } | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  /**
   * Answer a notice in place, through the same dispatcher the full notifications page uses —
   * a second copy of that switch is how one of them quietly stops matching the server.
   *
   * The notice is marked read whatever the outcome, including "no longer available": a stale
   * invitation is still something the reader has now dealt with and should stop being shown.
   */
  const handleNoticeAction = async (notice: UserNotice, index: number) => {
    const action = notice.actions?.[index]
    if (!action || busyAction) return

    // Taking ownership of an adventure moves real work; accepting a friend request does not.
    if (action.actionType === 'accept_adventure_transfer' &&
        !window.confirm('Take ownership of this adventure?')) return

    setBusyAction({ noticeId: notice.id, index })
    try {
      await executeNoticeAction(notice.type, action.actionType, action.actionData, notice.parsedData)
      if (!notice.read) markReadMutation.mutate(notice.id)
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      setActionMessage('Done.')
    } catch (err) {
      // A notification outlives what it points at, so "already answered" is ordinary.
      const status = (err as { response?: { status?: number } })?.response?.status
      setActionMessage(
        status === 404 || status === 400 || status === 410
          ? 'That’s no longer available — it may already have been answered.'
          : 'That didn’t work. Try again in a moment.'
      )
      if (status && [400, 404, 410].includes(status) && !notice.read) markReadMutation.mutate(notice.id)
    } finally {
      setBusyAction(null)
    }
  }

  const handleRowClick = (notice: UserNotice) => {
    if (!notice.read) markReadMutation.mutate(notice.id)
    onClose()
    // Deep-link a few common types directly to the page that lets the user
    // act on them. Everything else falls through to the general feed.
    const typeUpper = notice.type.toUpperCase()
    if (typeUpper === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase()) {
      navigate('/moderator-approvals')
      return
    }
    const groupTarget = groupSessionNoticeTarget(notice)
    if (groupTarget) {
      navigate(groupTarget.path, groupTarget.state ? { state: groupTarget.state } : undefined)
      return
    }
    if (notice.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST && notice.parsedData?.relatedId) {
      navigate(`/groups/${notice.parsedData.relatedId}?tab=requests`)
      return
    }
    // "X is live — go find it!" has to actually take you to X, or the reminder was
    // pointless. PascalCase key: see AdventureOpenedData.
    if (typeUpper === NoticeTypeIds.ADVENTURE_OPENED.toUpperCase() && notice.parsedData?.AdventureId) {
      navigate(`/adventures/${notice.parsedData.AdventureId}`)
      return
    }
    // "Somebody replied" is only useful if it lands on the reply. /discussions/:id resolves
    // which group / adventure / blog the discussion is read in — the notice only names it.
    if (typeUpper === NoticeTypeIds.DISCUSSION_REPLY.toUpperCase() && notice.parsedData?.DiscussionId) {
      const postId = notice.parsedData?.PostId
      navigate(`/discussions/${notice.parsedData.DiscussionId}${postId ? `?postId=${postId}` : ''}`)
      return
    }
    navigate('/notifications')
  }

  const handleSeeAll = () => {
    onClose()
    navigate('/notifications')
  }

  const handleMarkAllRead = async () => {
    const unread = (notices ?? []).filter((n) => !n.read)
    if (unread.length === 0) return
    await Promise.all(unread.map((n) => markNoticeAsRead(n.id)))
    queryClient.invalidateQueries({ queryKey: ['userNotices'] })
    queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          elevation: 4,
          sx: {
            mt: 1,
            width: 380,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={handleMarkAllRead}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open full page">
            <IconButton size="small" onClick={handleSeeAll}>
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && visibleNotices.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, color: 'text.secondary' }}>
            <NotificationsNone sx={{ fontSize: 40, opacity: 0.6 }} />
            <Typography variant="body2">You're all caught up.</Typography>
          </Box>
        )}

        {!isLoading && visibleNotices.map((notice) => (
          <Box
            key={notice.id}
            onClick={() => handleRowClick(notice)}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.25,
              cursor: 'pointer',
              bgcolor: notice.read ? 'transparent' : (
                theme.palette.mode === 'dark'
                  ? 'rgba(144,202,249,0.08)'
                  : 'rgba(25,118,210,0.06)'
              ),
              borderLeft: notice.read ? '3px solid transparent' : `3px solid ${theme.palette.primary.main}`,
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            })}
          >
            <Box
              sx={(theme) => ({
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: theme.palette.action.selected,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.primary',
                flexShrink: 0,
              })}
            >
              {getIconForType(notice)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: notice.read ? 500 : 700,
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {notice.title}
              </Typography>
              {notice.quickMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {notice.quickMessage}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}>
                {getTimeAgo(notice.created)}
              </Typography>

              {/* The server has always sent these; nothing used to render them, so an
                  invitation arrived with no way to answer it. Only actions this build knows
                  how to dispatch appear — an unknown one would be a button that does nothing.
                  Clicks stop here: answering must not also open whatever the row links to. */}
              {(notice.actions?.length ?? 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {notice.actions!.map((action, index) =>
                    SUPPORTED_ACTION_TYPES.has(action.actionType) ? (
                      <Button
                        key={`${action.actionType}-${index}`}
                        size="small"
                        variant={index === 0 ? 'contained' : 'outlined'}
                        disabled={busyAction !== null}
                        onClick={(e) => {
                          e.stopPropagation()
                          void handleNoticeAction(notice, index)
                        }}
                        sx={{ textTransform: 'none', py: 0.25, minWidth: 0 }}
                      >
                        {busyAction?.noticeId === notice.id && busyAction.index === index
                          ? 'Working…'
                          : action.label}
                      </Button>
                    ) : null
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Snackbar
        open={actionMessage !== null}
        autoHideDuration={4000}
        onClose={() => setActionMessage(null)}
        message={actionMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 1 }}>
        <Button fullWidth size="small" onClick={handleSeeAll}>
          See all notifications
        </Button>
      </Box>
    </Popover>
  )
}
